import React, { useRef, useState } from 'react';
import { Box, Typography } from '@mui/material';
import dayjs from 'dayjs';
import { type FormikProps, useFormik } from 'formik';
import {
  type ApolloClient,
  type ApolloQueryResult,
  type NormalizedCacheObject,
  useApolloClient,
} from '@apollo/react-hooks';
import { object, string } from 'yup';
import { ExtendPaymentDeadlineBox, type ExtendDeadlineFormikProps } from './';
import { EXTEND_REG_FEE_DDL } from '../lang';
import {
  Alert,
  type AlertDetails,
  Button,
  Dialog,
  type DialogDetails,
} from '../../../../../../components';
import { useUpdateRegistrationFeeDeadline } from '../../../../graphql/DRCAssessment/FunctionHub';
import { useAuth } from '../../../../../../contexts';
import {
  ApplicationViewList,
  type ApplicationViewListProps,
  type ApplicationViewTableDataItem,
  type FilterFields,
  type TableColumns,
} from '../../../../../Application/components';
import type {
  CustomAction,
  ApplicationViewTableFilterFields,
} from '../../../../../Application/components/ApplicationViewList/types';
import { checkInvalidDate } from '../../../../../Application/graphql';

type ExtendPaymentDeadlineProps = {
  filter: any;
  departmentLevel: boolean;
};

export const ExtendPaymentDeadline: React.FC<ExtendPaymentDeadlineProps> = (
  props: ExtendPaymentDeadlineProps,
) => {
  const { filter: listFilter, departmentLevel } = props;
  const { handleLoading } = useAuth();
  // default value
  const getRefetchRef = useRef<() => Promise<ApolloQueryResult<any>>>(
    async () => ({
      data: {},
      loading: false,
      networkStatus: 7,
      stale: false,
    }),
  );

  const [openAlert, setOpenAlert] = useState(false);
  const [alertDetails, setAlertDetails] = useState<AlertDetails>({
    alertContent: '',
    alertType: 'error',
  });

  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogDetails, setDialogDetails] = useState<
    DialogDetails & {
      component?:
        | ((formik: FormikProps<ExtendDeadlineFormikProps>) => React.ReactNode)
        | React.ReactNode;
    }
  >({
    title: '',
  });

  const [formikStep, setFormikStep] = useState<number>(0);

  const client = useApolloClient() as ApolloClient<NormalizedCacheObject>;

  // Extend Deadline
  const handleEdit = async (): Promise<void> => {
    setDialogDetails(dialogMappings.notification); // switch to notification dialog
    setFormikStep(1);
  };

  const handleConfirm = async (
    values: ExtendDeadlineFormikProps,
  ): Promise<void> => {
    await handleLoading(async () => {
      setFormikStep(0);
      setDialogOpen(false);
      const { applicationId, extendedDate } = values;
      try {
        await useUpdateRegistrationFeeDeadline(client, {
          applicationId,
          paymentDeadline: extendedDate?.toString(),
        });

        setAlertDetails({
          alertContent: EXTEND_REG_FEE_DDL.ALERT.SUCCESS,
          alertType: 'success',
        });
      } catch (e) {
        setAlertDetails({
          alertContent: `${EXTEND_REG_FEE_DDL.ALERT.ERROR} ${(e as Error).message}`,
          alertType: 'error',
        });
      }
      setOpenAlert(true);
    });
  };

  const formik = useFormik<ExtendDeadlineFormikProps>({
    initialValues: {
      applicationId: '',
      previousDate: undefined,
      extendedDate: undefined,
    },
    validationSchema: object({
      extendedDate: string().required('Required'),
    }),
    onSubmit: async (value: ExtendDeadlineFormikProps) => {
      void [
        async () => {
          await handleEdit();
        },
        async () => {
          await handleConfirm(value);
        },
      ][formikStep]();
    },
  });

  const dialogMappings = {
    extendDeadline: {
      title: EXTEND_REG_FEE_DDL.DIALOG.EXTEND_DDL.TITLE,
      confirmAction: EXTEND_REG_FEE_DDL.DIALOG.EXTEND_DDL.CONFIRM,
      cancelAction: EXTEND_REG_FEE_DDL.DIALOG.EXTEND_DDL.CANCEL,
      handleDialogConfirmAction: formik.submitForm,
      component: (
        formik: FormikProps<ExtendDeadlineFormikProps>,
      ): React.ReactNode => {
        return <ExtendPaymentDeadlineBox formik={formik} />;
      },
    },
    notification: {
      title: EXTEND_REG_FEE_DDL.DIALOG.NOTIFICATION.TITLE,
      confirmAction: EXTEND_REG_FEE_DDL.DIALOG.NOTIFICATION.CONFIRM,
      cancelAction: EXTEND_REG_FEE_DDL.DIALOG.NOTIFICATION.CANCEL,
      handleDialogConfirmAction: formik.submitForm,
      component: (
        <Typography>
          {EXTEND_REG_FEE_DDL.DIALOG.NOTIFICATION.COMPONENT.CONFIRM_MSG}
        </Typography>
      ),
    },
  };

  const handleOpenDialog = async (
    row: ApplicationViewTableDataItem,
    refetch: () => Promise<ApolloQueryResult<any>>,
  ): Promise<void> => {
    const { applicationId, registrationFeePaymentDeadline } = row;
    const paymentDeadline = checkInvalidDate(registrationFeePaymentDeadline)
      ? undefined // use undefined here because DatePicker's value accepts both Dayjs|undefined|null, but DatePicker's minDate/maxDate accepts ONLY Dayjs|undefined, here make it defensive to accidential usage of null
      : dayjs(registrationFeePaymentDeadline);

    await formik.setValues({
      applicationId,
      previousDate: paymentDeadline,
      extendedDate: undefined,
    });

    setDialogDetails(dialogMappings.extendDeadline);
    setDialogOpen(true);

    // Ref is acceptable here. Because currently update ref is triggered together with setState, the re-render must be triggered.
    // But as update ref.current will not trigger re-render itself,
    // there is a risk (if remove setDialogControl here) that after refetch updated, the latest value is not passed to component.
    getRefetchRef.current = refetch;
  };

  const customActions: CustomAction[] = [
    {
      icon: (
        <Button color="primary" variant="contained" className="!normal-case">
          {EXTEND_REG_FEE_DDL.EXTEND_BUTTON}
        </Button>
      ),
      title: EXTEND_REG_FEE_DDL.EXTEND_BUTTON,
      handleAction: handleOpenDialog,
      checkDisable: () => false,
    },
  ];

  const hiddenFilters: FilterFields[] = React.useMemo(
    () => [
      ...(departmentLevel
        ? ['department' as keyof ApplicationViewTableFilterFields]
        : []),
      'applicantIdentityType',
      'applicantLocalIndicator',
      'scholarshipAppliedByApplicant',
      'applicationStatus',
      'fundingSchemeStatus',
      'fundingScheme',
      'hkpfsNo',
      'dualPartnerUniversity',
      'jointpartnerUniversity',
      'partnerCategory',
      'gsAssignee',
    ],
    [],
  );

  const filterProps: ApplicationViewListProps['filterProps'] = {
    hiddenFilterFields: hiddenFilters,
  };
  const visibleColumns: TableColumns[] = React.useMemo(
    () => [
      'applicationNo',
      'applicantName',
      'department',
      'proposedChiefSupervisors',
      'applicantIdentityInfo',
      'applicantNationality',
      'entryYear',
      'applyProgramme',
      'hkpfsDisplayInformation',
      'researchAreas',
      'registrationFeePaymentDeadline',
      'applicationStatus',
    ],
    [],
  );

  const tableProps: ApplicationViewListProps['tableProps'] = {
    initialVisibleColumns: visibleColumns,
    listFilter,
    customActions,
    pickCommonActions: [],
  };

  return (
    <div>
      <Alert
        openAlert={openAlert}
        setOpenAlert={setOpenAlert}
        alertDetails={alertDetails}
      />
      <ApplicationViewList tableProps={tableProps} filterProps={filterProps} />

      <Dialog
        width="700px"
        openDialog={dialogOpen}
        dialogTitle={dialogDetails.title}
        dialogAction={dialogDetails.confirmAction}
        cancelAction={dialogDetails.cancelAction ?? undefined}
        onDialogAction={dialogDetails.handleDialogConfirmAction}
        closeAfterAction={false}
        onDialogClose={() => {
          setDialogOpen(false);
        }}
      >
        <Box className="mx-6 p-6 border border-gray-300 rounded-md">
          {typeof dialogDetails.component === 'function'
            ? dialogDetails?.component?.(formik)
            : dialogDetails.component}
        </Box>
      </Dialog>
    </div>
  );
};
